package mapsearch.buildings

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectType
import apiclient.mapsearch.SearchSpec
import apiclient.util.isNotNullOrEmpty
import auth.WorkspacesStore
import com.jillesvangurp.geo.GeoGeometry
import data.objects.ActiveObjectStore
import data.users.settings.LocalSettingsStore
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlin.math.max
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import map.MapStateStore
import maplibreGL.FormationMap
import maplibreGL.LngLat
import maplibreGL.MaplibreGeoJSON
import maplibreGL.MaplibreImageOverlayRotated
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.Marker
import maplibreGL.generateMapMarker
import maplibreGL.renderer.ADDITIONAL_GEOMETRY
import maplibreGL.toJs
import mapsearch.MapRenderOverrides
import mapsearch.MarkerGenerators.makeSvgMarkerForMap
import mapsearch.markerEventListener
import mapsearch.tiles
import model.Building
import mu.KotlinLogging
import search.getSvgIconOptions
import svgmarker.MarkerSize

private val log = KotlinLogging.logger("BuildingSearchSpecStore")

class BuildingSearchSpecStore : RootStore<SearchSpec?>(null, Job()) {

    private val currentRenderedBuildingMarkers = mutableMapOf<String, Marker>()
    private val currentRenderedImageOverlays = mutableMapOf<String, List<MaplibreImageOverlayRotated>>()
    private val currentRenderedGeoJSONFloors = mutableMapOf<String, List<MaplibreGeoJSON>>()
    private val currentRenderedUnits = mutableMapOf<String, List<MaplibreGeoJSON>>()

    fun initialize() {
        startBuildingsSearch()
        processWorkspaceChanges()
        processFloorChanges()
        processActiveBuildingChange()
        processOverrideChange()
    }

    fun resetBuildings() {
        withKoin {
            val activeBuildingStore: ActiveBuildingStore = get()
            val currentBuildingsStore: CurrentBuildingsStore = get()
            val buildingLookUpStore: BuildingLookUpStore = get()
            val buildingTilesLookUpStore: BuildingTilesLookup = get()
            val activeBuildingOverrideStore: ActiveBuildingOverrideStore = get()
            val activeFloorLevelStore: ActiveFloorLevelStore = get()

            currentRenderedBuildingMarkers.clear()
            currentRenderedImageOverlays.clear()
            currentRenderedGeoJSONFloors.clear()
            currentRenderedUnits.clear()

            buildingTilesLookUpStore.reset()
            buildingLookUpStore.reset()
            currentBuildingsStore.reset()
            activeBuildingOverrideStore.reset(Unit)
            activeFloorLevelStore.update(null)
            activeBuildingStore.update(null)
            update(null)
        }
    }

    private fun processWorkspaceChanges() {
        withKoin {
            val workspacesStore = get<WorkspacesStore>()

            workspacesStore.data handledBy { groups ->
                val ownerIds = groups.map { g -> g.groupId }
                console.log("^^ workspacesStore changed (${ownerIds}, current: ${current?.ownerIds}) -> should remove all buildings")
                try {
                    // remove all buildings, if ownerIds changed
                    if (ownerIds != current?.ownerIds) {
                        console.log("^^ remove all buildings(${currentRenderedBuildingMarkers.keys}), because ownerIds changed")
                        console.log("^^ current img floors (${currentRenderedImageOverlays.keys})")
                        removeBuildingsFromMap(currentRenderedBuildingMarkers.keys)
                    }
                    update(
                        SearchSpec(
                            ownerIds = ownerIds,
                            objectTypes = listOf(ObjectType.Building),
                        ),
                    )
                } catch (e: Exception) {
                    log.error(e) { "Error handling groups update (groups: ${groups.size}) $groups" }
                }
            }
        }
    }

    private fun processActiveBuildingChange() {
        withKoin {
            val activeBuildingStore: ActiveBuildingStore = get()
            val currentBuildingsStore: CurrentBuildingsStore = get()
            val buildingLookUpStore: BuildingLookUpStore = get()
            val activeBuildingOverrideStore: ActiveBuildingOverrideStore = get()
            val activeFloorLevelStore: ActiveFloorLevelStore = get()

            activeBuildingStore.data handledBy { buildingId ->
                buildingId?.let { id ->
                    (currentBuildingsStore.current[id]
                        ?: buildingLookUpStore.current[id])?.let { building ->
                        val level = if (activeBuildingOverrideStore.current?.buildingId == id) {
                            val levelOverwrite = building.getFloorLevel(activeBuildingOverrideStore.current?.floorId)
                            activeBuildingOverrideStore.reset(Unit)
                            levelOverwrite
                        } else {
                            (building.activeFloorLevel ?: building.defaultFloorLevel)
                        }
                        level?.let { floorLevel ->
                            activeFloorLevelStore.update(floorLevel)
                            val updatedBuilding = mapOf(
                                id to building.copy(
                                    activeFloorLevel = floorLevel,
                                    activeFloorIds = building.floorData?.get(floorLevel)?.map { it.floor.id },
                                ),
                            )
                            currentBuildingsStore.updateBuildings(updatedBuilding)
                            buildingLookUpStore.updateBuildings(updatedBuilding)
                        }
                    }
                }
            }
        }
    }

    private fun processFloorChanges() {
        withKoin {
            val activeFloorLevelStore: ActiveFloorLevelStore = get()
            // Re-render floors of single active building when floor level changes
            activeFloorLevelStore.data handledBy { floorLevel ->
                floorLevel?.let {
                    setFloorLevelForBuilding(floorLevel)
                }
            }
        }
    }

    private fun processOverrideChange() {
        withKoin {
            val mapRenderOverrides: MapRenderOverrides = get()
            val buildingLookUpStore: BuildingLookUpStore = get()

            mapRenderOverrides.pathToolActive.data handledBy { p ->
                currentRenderedBuildingMarkers.keys.mapNotNull { buildingId ->
                    buildingLookUpStore.current[buildingId]?.geoObjectDetails
                }.let { buildings ->
                    reRenderMultipleBuildingMarkers(buildings)
                }
            }
        }
    }

    private fun reRenderMultipleBuildingMarkers(buildings: List<GeoObjectDetails>) {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map

            buildings.forEach { building ->
                console.log("Re-render Building", building.title)
                currentRenderedBuildingMarkers.remove(building.id)?.let { marker ->
                    marker.remove()
                    createBuildingMarker(building)?.let { newMarker ->
                        currentRenderedBuildingMarkers[building.id] = newMarker
                        newMarker.addTo(map)
                    }
                }
            }
        }
    }

    private fun detectNearestBuilding(buildings: List<GeoObjectDetails>) {
        withKoin {
            val activeBuildingStore: ActiveBuildingStore = get()
            val mapStateStore: MapStateStore = get()

            mapStateStore.current?.center?.let { center ->
                val nearestBuilding = buildings.minByOrNull { building ->
                    GeoGeometry.distance(
                        building.latLon.lat,
                        building.latLon.lon,
                        center.lat,
                        center.lon,
                    )
                }
//                console.log("NEAREST BUILDING ->", nearestBuilding?.title ?: "no building near MapCenter")
                activeBuildingStore.update(nearestBuilding?.id)
            }
        }
    }

    private fun startBuildingsSearch() {
        withKoin {
            val mapStateStore: MapStateStore = get()
            val buildingLookupStore: BuildingLookUpStore = get()
            val buildingTilesLookup: BuildingTilesLookup = get()
            val currentBuildingsStore: CurrentBuildingsStore = get()
            val activeBuildingOverrideStore: ActiveBuildingOverrideStore = get()
            val activeFloorLevelStore: ActiveFloorLevelStore = get()

            combine(
                buildingTilesLookup.data,
                data,
                mapStateStore.data.debounce(50),
            ) { tiles, spec, map -> Triple(tiles, spec, map) } handledBy { (tilesLookup, searchSpec, mapState) ->
                try {
                    if (mapState != null && searchSpec?.ownerIds.isNotNullOrEmpty()) {
                        // search buildings with reduced zoom level to keep them in results, even when zoomed in very far
                        val highLevelTiles = mapState.copy(
                            zoom = max(mapState.zoom.toInt() - 7, 0).toDouble(),
                        ).tiles().second
                        val actualLevelTiles = mapState.tiles().second

                        val ids = (highLevelTiles + actualLevelTiles).flatMap { tile ->
                            tilesLookup[tile].orEmpty()
                        }
//                        console.log("NEW SEARCH Ids -> ", ids.toString())
                        val uniqueBuildings = ids.mapNotNull { id ->
                            (currentBuildingsStore.current[id]
                                ?: buildingLookupStore.current[id])?.let { building ->
                                activeBuildingOverrideStore.current?.let { overwrite ->
                                    if (overwrite.buildingId == id) {
                                        val levelOverwrite = building.getFloorLevel(overwrite.floorId)
                                        activeBuildingOverrideStore.reset(Unit)
                                        activeFloorLevelStore.update(levelOverwrite)
                                        building.copy(
                                            activeFloorLevel = levelOverwrite,
                                            activeFloorIds = building.floorData?.get(levelOverwrite)
                                                ?.map { it.floor.id },
                                        )
                                    } else building
                                } ?: building
                            }
                        }
//                        console.log("NEW SEARCH Buildings -> ", uniqueBuildings.map { it.buildingName }.toString())
                        detectNearestBuilding(uniqueBuildings.map { it.geoObjectDetails })
                        currentBuildingsStore.setBuildings(uniqueBuildings)
                        buildingLookupStore.updateBuildings(uniqueBuildings.associateBy { it.buildingId })
                        renderBuildings(uniqueBuildings.associateBy { it.buildingId })
                    }
                } catch (e: Exception) {
                    log.error(e) { "Error handling new searchSpec" }
                }
            }
        }
    }

    fun setFloorLevelForBuildingByFloorId(floorId: String, buildingId: String? = null) {
        withKoin {
            val buildingLookUpStore: BuildingLookUpStore = get()
            val currentBuildingsStore: CurrentBuildingsStore = get()
            val activeBuildingStore: ActiveBuildingStore = get()

            (currentBuildingsStore.current[buildingId ?: activeBuildingStore.current]
                ?: buildingLookUpStore.current[buildingId ?: activeBuildingStore.current]
                )?.getFloorLevel(floorId)?.let { floorLevel ->
                    setFloorLevelForBuilding(floorLevel, buildingId)
                }
        }
    }

    private fun setFloorLevelForBuilding(floorLevel: Double, buildingId: String? = null) {
        withKoin {
            val buildingLookUpStore: BuildingLookUpStore = get()
            val currentBuildingsStore: CurrentBuildingsStore = get()
            val activeBuildingStore: ActiveBuildingStore = get()
            val activeFloorLevelStore: ActiveFloorLevelStore = get()

            (currentBuildingsStore.current[buildingId ?: activeBuildingStore.current]
                ?: buildingLookUpStore.current[buildingId ?: activeBuildingStore.current])?.let { building ->
                val updatedBuilding = building.copy(
                    activeFloorLevel = floorLevel,
                    activeFloorIds = building.floorData?.get(floorLevel)?.map { floor -> floor.floor.id },
                )
                // update active Level and floorIds of building in BuildingLookUpStore & currentBuildingsStore
                val updatedBuildingEntry = mapOf(building.buildingId to updatedBuilding)
                buildingLookUpStore.updateBuildings(updatedBuildingEntry)
                currentBuildingsStore.updateBuildings(updatedBuildingEntry)
                activeFloorLevelStore.update(floorLevel)
                // render floors
                reRenderFloorsAndUnitsByLevel(building, floorLevel)
            }
        }
    }

    fun reRenderSingleBuildingMarker(building: GeoObjectDetails) {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map
            currentRenderedBuildingMarkers.remove(building.id)?.let { marker ->
                marker.remove()
                createBuildingMarker(building)?.let { newMarker ->
                    currentRenderedBuildingMarkers[building.id] = newMarker
                    newMarker.addTo(map)
                }
            }
        }
    }

    /**
     * Creates the object for mapLibre to render the marker of a building,
     * and puts it into the "currentRenderedBuildingMarkers"-list
     */
    private fun createBuildingMarker(building: GeoObjectDetails): Marker? {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            val localSettingsStore: LocalSettingsStore = get()
            val mapRenderOverrides: MapRenderOverrides = get()

            return getSvgIconOptions(
                building,
                desaturated = mapRenderOverrides.pathToolActive.current,
            )?.let { svgIconOptions ->
                val markerOptions = MaplibreMarkerOptions(
                    element = makeSvgMarkerForMap(
                        id = building.id,
                        title = building.title,
                        svgIconOptions = svgIconOptions.copy(size = MarkerSize.S),
                        objectType = ObjectType.Building,
                        active = activeObjectStore.current.id == building.id,
                        showTitle = !localSettingsStore.current.hideMarkerTitles,
                    ).also { element ->
                        if (!svgIconOptions.desaturated) { // make non-clickable when desaturated (= disabled)
                            element.addEventListener("click", markerEventListener)
                        }
                    },
                )
                val newMarker = Marker(markerOptions.toJs())
                newMarker.setLngLat(LngLat(building.latLon.lon, building.latLon.lat))
                newMarker
            }
        }
    }

    /**
     * Creates the objects for mapLibre to render floors and units of a building,
     * (optional with specified floor level)
     * and puts them into the
     * "currentRenderedImageOverlays"-,
     * "currentRenderedGeoJSONFloors"- and
     * "currentRenderedUnits"-lists
     */
    private fun createFloorsAndUnitsForSingleBuilding(building: Building, withLevel: Double? = null, renderAll: Boolean = false) {
        val floors = ((withLevel ?: building.activeFloorLevel)
            ?: building.defaultFloorLevel
            )?.let { floorLevel ->
                building.floorData?.get(floorLevel).orEmpty()
            } ?: emptyList()

        val imageOverlays = mutableListOf<MaplibreImageOverlayRotated>()
        val geoJSONFloors = mutableListOf<MaplibreGeoJSON>()
        val geoJSONUnits = mutableListOf<MaplibreGeoJSON>()

        floors.map { floor ->
            val marker = generateMapMarker(floor.floor)
            imageOverlays += marker?.imageOverlays.orEmpty()
            geoJSONFloors += marker?.geoJSONs.orEmpty()
            geoJSONUnits += floor.units?.let { units ->
                units.map { unit -> generateMapMarker(unit) }
            }?.mapNotNull { it?.geoJSONs }?.flatten()?.distinctBy { it.id }.orEmpty()
        }

        // Save new rendered image & geoJSON overlays for floors and units
        currentRenderedImageOverlays[building.buildingId] = imageOverlays
        currentRenderedGeoJSONFloors[building.buildingId] = geoJSONFloors
        currentRenderedUnits[building.buildingId] = geoJSONUnits

        if (renderAll) {
            renderFloorsAndUnits()
        }
    }

    /**
     * Renders all floors and units from the
     * "currentRenderedImageOverlays"-,
     * "currentRenderedGeoJSONFloors"- and
     * "currentRenderedUnits"-lists
     */
    private fun renderFloorsAndUnits() {
        withKoin {
            val formationMap: FormationMap = get()
            // TODO find way to render floors of active building last (on top)
            formationMap.updateImageOverlayLayer(
                currentRenderedImageOverlays.flatMap { it.value },
            )
            formationMap.geoJSONAdd(
                (currentRenderedGeoJSONFloors.flatMap { it.value } + currentRenderedUnits.flatMap { it.value }).map { it.feature },
                ADDITIONAL_GEOMETRY,
            )
        }
    }

    /**
     * Renders the floors and units for a specified floor level of a building
     */
    private fun reRenderFloorsAndUnitsByLevel(building: Building, floorLevel: Double? = null) {
        log.info { "Re-render ${building.buildingName} with active floor level $floorLevel" }
        // replace Feature -> floors/units of single building
        removeFloorsAndUnitsFromMap(building.buildingId)
        createFloorsAndUnitsForSingleBuilding(building, withLevel = floorLevel, renderAll = true)
//        renderFloorsAndUnits()
    }

    /**
     * Removes the currently rendered floors and units of a building
     */
    private fun removeFloorsAndUnitsFromMap(buildingId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            console.log("^^ remove floors for $buildingId ${currentRenderedImageOverlays.map { (k, v) -> "$k -> ${v.map { it.id }}" }}")
            currentRenderedImageOverlays.remove(buildingId)?.let { imageOverlays ->
                imageOverlays.forEach {
                    formationMap.removeImageOverlayLayer(it.id)
                }
            }
            currentRenderedGeoJSONFloors.remove(buildingId)?.let { floorGeoJSONs ->
                formationMap.geoJSONRemove(
                    floorGeoJSONs.map { it.id }.toTypedArray(),
                    ADDITIONAL_GEOMETRY,
                )
            }
            currentRenderedUnits.remove(buildingId)?.let { unitGeoJSONs ->
                formationMap.geoJSONRemove(
                    unitGeoJSONs.map { it.id }.toTypedArray(),
                    ADDITIONAL_GEOMETRY,
                )
            }
        }
    }

    /**
     * Removes a building completely from the map, including marker, floors and units
     */
    private fun removeBuildingFromMap(buildingId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            if (formationMap.map != null) {
                currentRenderedBuildingMarkers.remove(buildingId)?.remove()
            }
            removeFloorsAndUnitsFromMap(buildingId)
        }
    }

    private fun removeBuildingsFromMap(buildingIds: Set<String>) {
        buildingIds.forEach { buildingId ->
            removeBuildingFromMap(buildingId)
        }
    }

    /**
     * Main function to render all building search results
     */
    private fun renderBuildings(buildings: Map<String, Building>) {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map

            if (map != null) {
//            console.log("§ RENDER BUILDING RESULTS ->", buildings.map { it.value.buildingName }.toString())
                // Remove all Building markers that should not be rendered anymore
                (currentRenderedBuildingMarkers.keys - buildings.keys).forEach { id ->
                    removeBuildingFromMap(id)
                }
                // Render new Buildings that are not on the map, yet
                val newBuildings = buildings.filter { (id, _) -> id !in currentRenderedBuildingMarkers.keys }
                newBuildings.forEach { (buildingId, building) ->
                    // Marker
                    createBuildingMarker(building.geoObjectDetails)?.let { newMarker ->
                        currentRenderedBuildingMarkers[buildingId] = newMarker
                        newMarker.addTo(map)
                    }
                    // Floor and Units
                    createFloorsAndUnitsForSingleBuilding(building)
                }.also {
                    renderFloorsAndUnits()
                }
            }
        }
    }
}
